import { AuthContext } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { useEffect, useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import axios from 'axios';
import Welcome from '../../components/Header/Welcome';
import { AdminNav } from '../../components';
import { formatDestinationName as formatDest } from '../../utils/destination';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const { token, currentUser } = useContext(AuthContext);
  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts : enTexts;

  const [isLoading, setIsLoading] = useState(true);
  const [filterDestination, setFilterDestination] = useState('');
  const [filterDateStr, setFilterDateStr] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const formatDestinationName = dest => (dest ? formatDest(dest, texts) : '-');

  const getLeads = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(response.data);
      setFilteredLeads(response.data);

      const uniqueDest = [];
      response.data.forEach(lead => {
        if (
          lead.destination &&
          !uniqueDest.some(
            d =>
              d.name === lead.destination.name &&
              d.year === lead.destination.year &&
              d.semester === lead.destination.semester
          )
        ) {
          uniqueDest.push(lead.destination);
        }
      });
      setDestinations(uniqueDest);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...leads];

    if (filterDestination) {
      result = result.filter(lead => formatDestinationName(lead.destination) === filterDestination);
    }

    if (filterDateStr) {
      result = result.filter(lead => {
        if (!lead.createdAt) return false;
        const dateObj = new Date(lead.createdAt);
        const isoDate = dateObj.toISOString().split('T')[0];
        return isoDate === filterDateStr;
      });
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'destination') {
          aVal = formatDestinationName(a.destination);
          bVal = formatDestinationName(b.destination);
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredLeads(result);
  };

  const requestSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    applyFilters();
  }, [filterDestination, filterDateStr, sortConfig, leads]);

  useEffect(() => {
    if (token) {
      getLeads();
    }
  }, [token]);

  if (!currentUser?.isAdmin) {
    return <Navigate to="/admin" />;
  }

  return (
    <>
      <Welcome />

      <div className="container py-4 mb-5" style={{ minHeight: '80vh' }}>
        <AdminNav />

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
          <div>
            <h2 className="fw-bold text-sen mb-1 d-flex align-items-center gap-2">
              <i className="fa fa-whatsapp"></i>
              <span>
                {texts.admin_common.list_of} {texts.admin.menu_leads}
              </span>
            </h2>
            <span className="text-muted small">
              {lang === 'es'
                ? 'Monitorea los estudiantes interesados y códigos OTP verificados para acceso a grupos de WhatsApp.'
                : 'Monitor student leads and OTP verification status for destination WhatsApp communities.'}
            </span>
          </div>
          <div className="mt-3 mt-sm-0">
            <span className="badge bg-sen px-3 py-2 rounded-pill fs-6 shadow-sm">
              <i className="fa fa-users me-1"></i> {filteredLeads.length} Leads
            </span>
          </div>
        </div>

        {/* Filter Card */}
        <div className="card border-0 shadow-sm rounded-4 bg-white mb-4">
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-5">
                <label className="form-label small fw-bold text-secondary">
                  {texts.admin.destination}
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0 text-muted">
                    <i className="fa fa-map-marker"></i>
                  </span>
                  <select
                    className="form-select bg-light border-start-0"
                    value={filterDestination}
                    onChange={e => setFilterDestination(e.target.value)}
                  >
                    <option value="">{texts.admin.all_destinations}</option>
                    {destinations.map((d, i) => (
                      <option key={i} value={formatDestinationName(d)}>
                        {formatDestinationName(d)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-secondary">
                  {texts.admin.date}
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0 text-muted">
                    <i className="fa fa-calendar"></i>
                  </span>
                  <input
                    type="date"
                    className="form-control bg-light border-start-0"
                    value={filterDateStr}
                    onChange={e => setFilterDateStr(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-md-3 text-md-end">
                <button
                  className="btn btn-outline-secondary w-100 py-2 rounded-3 fw-semibold"
                  onClick={() => {
                    setFilterDestination('');
                    setFilterDateStr('');
                  }}
                >
                  <i className="fa fa-times me-1"></i>
                  {texts.admin.clear_filters}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th
                      className="py-3 px-4 text-secondary small fw-bold text-uppercase cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => requestSort('name')}
                    >
                      {texts.admin_common.name}{' '}
                      {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className="py-3 px-3 text-secondary small fw-bold text-uppercase cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => requestSort('email')}
                    >
                      {texts.admin_common.email}{' '}
                      {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className="py-3 px-3 text-secondary small fw-bold text-uppercase cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => requestSort('destination')}
                    >
                      {texts.admin.destination}{' '}
                      {sortConfig.key === 'destination' &&
                        (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="py-3 px-3 text-secondary small fw-bold text-uppercase">
                      {texts.admin_common.type}
                    </th>
                    <th
                      className="py-3 px-3 text-secondary small fw-bold text-uppercase cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => requestSort('createdAt')}
                    >
                      Fecha OTP{' '}
                      {sortConfig.key === 'createdAt' &&
                        (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className="py-3 px-4 text-secondary small fw-bold text-uppercase text-end cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => requestSort('is_verified')}
                    >
                      Estado OTP{' '}
                      {sortConfig.key === 'is_verified' &&
                        (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        <i className="fa fa-inbox fs-3 d-block mb-2 text-secondary"></i>
                        {texts.admin_common.no_records}
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map(lead => (
                      <tr key={lead.id}>
                        <td className="py-3 px-4 fw-semibold text-dark">{lead.name}</td>
                        <td className="py-3 px-3 text-muted small">{lead.email}</td>
                        <td className="py-3 px-3">
                          <span className="badge bg-light text-primary border fw-semibold">
                            {formatDestinationName(lead.destination)}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span
                            className={`badge ${lead.type === 'country' ? 'bg-primary' : 'bg-secondary'}`}
                          >
                            {lead.type === 'country' ? 'País' : 'Ciudad'}
                          </span>
                        </td>
                        <td className="py-3 px-3 small text-muted">
                          {lead.createdAt ? new Date(lead.createdAt).toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-4 text-end">
                          {lead.is_verified ? (
                            <span className="badge bg-success px-3 py-2 rounded-pill">
                              <i className="fa fa-check-circle me-1"></i>
                              {texts.admin_common.verified || 'Verificado'}
                            </span>
                          ) : (
                            <span className="badge bg-warning text-dark px-3 py-2 rounded-pill">
                              <i className="fa fa-clock-o me-1"></i>
                              {texts.admin_common.pending || 'Pendiente'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Leads;
